import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { AiArtifact, updateAiProjectArtifact } from 'services'
import { Button } from 'stories/components'
import { MarkdownRender } from 'stories/components/MarkdownRender/MarkdownRender'

const EXTENSION_BY_KIND: Record<string, string> = {
  markdown: 'md',
  text: 'txt',
  code: 'txt',
  html: 'html',
  json: 'json',
}

interface ArtifactViewerProps {
  projectId: number
  artifact: AiArtifact
  onClose: () => void
  onSaved: () => void
}

/**
 * Read and revise one artifact. HTML renders in a sandboxed frame - an artifact is model-authored
 * content, so it gets no script access, no same-origin, and no reach into the app around it.
 */
export function ArtifactViewer({ projectId, artifact, onClose, onSaved }: ArtifactViewerProps) {
  const [content, setContent] = useState(artifact.content || '')
  const [title, setTitle] = useState(artifact.title)
  const [isEditing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setContent(artifact.content || '')
    setTitle(artifact.title)
    setEditing(false)
  }, [artifact])

  const onSave = async () => {
    setSaving(true)
    try {
      await updateAiProjectArtifact(projectId, artifact.id, { title, content })
      setEditing(false)
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const onDownload = () => {
    const extension = EXTENSION_BY_KIND[artifact.kind] || 'txt'
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${title.replace(/[^\w\-. ]+/g, '_') || 'artifact'}.${extension}`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-[64rem] max-w-full h-[85vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200">
          <div className="min-w-0">
            {isEditing ? (
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-lg font-semibold border-b border-gray-300 focus:border-shade-blue focus:outline-none w-full"
              />
            ) : (
              <h3 className="text-lg font-semibold truncate">{title}</h3>
            )}
            <p className="text-xs text-gray-500 mt-0.5">
              {artifact.kind}
              {artifact.language ? ` · ${artifact.language}` : ''} · v{artifact.version} ·{' '}
              {artifact.source === 'assistant' ? 'created by the assistant' : 'created by you'}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isEditing ? (
              <>
                <Button size="sm" color="gray" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" onClick={onSave} loading={saving}>
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" color="gray" onClick={onDownload}>
                  <span className="flex items-center gap-1.5">
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    Download
                  </span>
                </Button>
                <Button size="sm" onClick={() => setEditing(true)}>
                  Edit
                </Button>
              </>
            )}
            <button className="p-1 text-gray-400 hover:text-gray-700" onClick={onClose} aria-label="Close">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {isEditing ? (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-full min-h-[24rem] font-mono text-sm border border-gray-300 rounded-md p-3 focus:border-shade-blue focus:outline-none"
            />
          ) : (
            <ArtifactBody kind={artifact.kind} content={content} />
          )}
        </div>
      </div>
    </div>
  )
}

function ArtifactBody({ kind, content }: { kind: string; content: string }) {
  if (kind === 'html') {
    return (
      <iframe
        title="Artifact preview"
        sandbox=""
        srcDoc={content}
        className="w-full h-full min-h-[24rem] border border-gray-200 rounded-md bg-white"
      />
    )
  }
  if (kind === 'code' || kind === 'json') {
    return (
      <pre className="text-sm bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto whitespace-pre-wrap">
        <code>{content}</code>
      </pre>
    )
  }
  if (kind === 'text') return <p className="text-sm whitespace-pre-wrap text-gray-800">{content}</p>
  return <MarkdownRender text={content} className="text-sm text-gray-800" />
}
